import { useEffect, useState } from 'react';

import { Navigation03Icon, PencilEdit01Icon } from '@hugeicons/core-free-icons';
import { EMAIL_TEMPLATE_TYPES } from 'constants/email-config';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { AnimatedModal } from 'components/AnimatedModal/AnimatedModal';
import { EmailTemplateStep } from 'components/EmailTemplateStep/EmailTemplateStep';
import { Button, InputGroup, MultipleChoice, Select } from 'components/FormComponents';

export const NewRefereeModal = ({
  visible,
  candidateForms = [],
  refereeForms = [],
  brand = {},
  onSelect = () => {},
  onClose = () => {},
}) => {
  const { t } = useTranslation();
  const { control, register, handleSubmit, watch, reset } = useForm();
  const [askCandidateTemplateId, setAskCandidateTemplateId] = useState(null);

  const candidateFormIdValue = watch('candidate_form_id');
  const refereeFormIdValue = watch('referee_form_id');
  const inputType = watch('input_type');

  useEffect(() => {
    if (!visible) {
      setAskCandidateTemplateId(null);
      reset();
    }
  }, [visible, reset]);

  useEffect(() => {
    if (inputType !== 'ask_candidate') {
      setAskCandidateTemplateId(null);
    }
  }, [inputType]);

  const onSubmit = data => {
    if (data.input_type === 'ask_candidate') {
      onSelect({
        ...data,
        template_id: askCandidateTemplateId,
      });
      return;
    }
    onSelect(data);
  };

  const formBody = (
    <>
      <div className="card">
        <div className="card">
          <InputGroup title="Candidate Form">
            <Select
              name="candidate_form_id"
              register={register}
              value={candidateFormIdValue}
              useDefault>
              {candidateForms.map(candidateForm => (
                <Select.Item value={candidateForm.id} key={candidateForm.id}>
                  {candidateForm.name}
                </Select.Item>
              ))}
            </Select>
          </InputGroup>

          <InputGroup title="Referee Form" className="u-margin-bottom--0">
            <Select
              name="referee_form_id"
              register={register}
              value={refereeFormIdValue}
              useDefault>
              {refereeForms.map(refereeForm => (
                <Select.Item value={refereeForm.id} key={refereeForm.id}>
                  {refereeForm.name}
                </Select.Item>
              ))}
            </Select>
          </InputGroup>
        </div>

        <Controller
          name="input_type"
          defaultValue="enter_details"
          control={control}
          render={({ field: { onChange, value } }) => (
            <MultipleChoice
              className="u-margin-top"
              value={value}
              options={{
                options: [
                  {
                    name: 'Enter Referee Details Yourself',
                    description: "Manually enter the referee's details into Checkmate.",
                    value: 'enter_details',
                    icon: PencilEdit01Icon,
                  },
                  {
                    name: 'Ask Candidate to Provide Details',
                    description:
                      "We'll notify the candidate to add their referee's information directly.",
                    value: 'ask_candidate',
                    icon: Navigation03Icon,
                  },
                ],
              }}
              onChange={onChange}
              radioButtonStyle
              prefixIcon
            />
          )}
        />
      </div>

      {inputType === 'ask_candidate' && (
        <div className="mt-6">
          <EmailTemplateStep
            brand={brand}
            category="candidate"
            standardName={t('check.askCandidateNewRefereeEmail.emailStandardName')}
            standardDescription={t('check.askCandidateNewRefereeEmail.emailStandardDescription')}
            previewStandardMessage={t('check.askCandidateNewRefereeEmail.emailPreviewStandard')}
            type={EMAIL_TEMPLATE_TYPES.NEW_REFEREE_NOMINATION}
            hideFooter
            onSelectedTemplateIdChange={setAskCandidateTemplateId}
          />
        </div>
      )}
    </>
  );

  const formActions = compactTop => (
    <div
      className={
        compactTop
          ? 'd-flex justify-content-between'
          : 'd-flex justify-content-between u-margin-top--large'
      }>
      <Button className="u-width-100" onClick={onClose} type="secondary">
        Cancel
      </Button>
      <Button className="u-width-100 u-margin-left--large" submit>
        Add Referee
      </Button>
    </div>
  );

  return (
    <AnimatedModal
      title="Add Referee"
      description="Please choose the candidate & referee forms you'd like to send, and let us know how you'd prefer to provide the details for this additional referee."
      visible={visible}
      onClose={onClose}
      noPadding={inputType === 'ask_candidate'}
      className="u-min-width-small">
      <form
        onSubmit={handleSubmit(onSubmit)}
        className={
          inputType === 'ask_candidate'
            ? 'flex flex-col max-h-[min(85vh,calc(100vh-8rem))]'
            : undefined
        }>
        {inputType === 'ask_candidate' ? (
          <>
            <div className="overflow-y-auto flex-1 min-h-0 px-6 pt-2 pb-4">{formBody}</div>
            <div className="px-6 pb-6 pt-4 border-t border-neutral-4 shrink-0">
              {formActions(true)}
            </div>
          </>
        ) : (
          <>
            {formBody}
            {formActions(false)}
          </>
        )}
      </form>
    </AnimatedModal>
  );
};
